// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

type GroupsSortBy = components["schemas"]["GroupsSortByEnum"];
type SortOrder = components["schemas"]["HttpSortingOrderEnum"];

export interface GroupRow {
  groupId: number;
  name: string;
  description: string;
  noOfUsers: number;
  createdAt: string;
}

export interface GroupDetail {
  group: GroupRow;
  permissions: string[];
}

interface GroupsListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: GroupsSortBy;
  orderBy?: SortOrder;
}

export function useGroupsQuery(params: GroupsListParams = {}) {
  const { page = 1, limit = 20, search, sortBy, orderBy } = params;

  return useQuery({
    queryKey: ["groups", "list", { page, limit, search, sortBy, orderBy }],
    queryFn: async () => {
      const res = await api.GET("/v1/groups", {
        params: {
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
          },
        },
      });
      return res.data as unknown as { groups: GroupRow[]; count: number };
    },
  });
}

export function useGroupDetailQuery(groupName: string) {
  return useQuery({
    queryKey: ["groups", "detail", groupName],
    queryFn: async () => {
      const res = await api.GET("/v1/groups/{group_name}", {
        params: { path: { group_name: groupName } },
      });
      if (res.error) throw new Error("Failed to load group");
      return res.data as unknown as GroupDetail;
    },
    enabled: !!groupName,
  });
}

// --- Create ---

export interface CreateGroupInput {
  name: string;
  description: string;
}

export function useCreateGroupMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateGroupInput) => {
      const res = await api.POST("/v1/groups", {
        body: { name: input.name, description: input.description },
      });
      if (res.error) throw new Error("Failed to create group");
      return res.data as unknown as { group: GroupRow };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

// --- Edit (permissions / description) ---

export function useUpdateGroupMutation(groupName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["GroupsEditRequest"]) => {
      const res = await api.PATCH("/v1/groups/{group_name}", {
        params: { path: { group_name: groupName } },
        body,
      });
      if (res.error) throw new Error("Failed to update group");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}
